import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Switch, FormControlLabel, Box, InputAdornment,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { TestPaperConfig, TestScore } from '../../../types/admissions';

interface Props {
  open: boolean;
  applicationId: string;
  papers: TestPaperConfig[];
  existingScores: TestScore[];
  /** "Pass mark 40 of 100" — shown under the title. */
  passNote?: string;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}

type Row = { paper: TestPaperConfig; value: string; absent: boolean };

/**
 * Marks per paper. Papers can be saved as they come in — leave a paper blank
 * to mark it later. Once every paper (and interview round) has marks, the
 * application moves to Assessed by itself.
 */
const TestScoresDialog: React.FC<Props> = ({ open, applicationId, papers, existingScores, passNote, onCancel, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState<Row[]>(() => {
    const prev = new Map(existingScores.map(s => [s.paperKey, s]));
    return papers.map(p => {
      const s = prev.get(p.key);
      return { paper: p, value: s && !s.absent ? String(s.obtained) : '', absent: !!s?.absent };
    });
  });

  const errorFor = (r: Row): string | null => {
    if (r.absent || r.value.trim() === '') return null;
    const n = Number(r.value);
    if (!Number.isFinite(n) || n < 0 || n > r.paper.maxMarks) return `0 – ${r.paper.maxMarks}`;
    return null;
  };
  const filled = rows.filter(r => r.absent || r.value.trim() !== '');
  const invalid = rows.some(r => errorFor(r));

  const set = (idx: number, patch: Partial<Row>) => setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.recordTestScores(applicationId, filled.map(r => ({
        paperKey: r.paper.key,
        obtained: r.absent ? null : Number(r.value),
        absent: r.absent,
      })));
      enqueueSnackbar('Test marks saved', { variant: 'success' });
      await onSaved();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the marks', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>Test marks</Typography>
        {passNote && <Typography variant="body2" color="text.secondary">{passNote}</Typography>}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 2 }}>
          {rows.map((r, idx) => (
            <Stack key={r.paper.key} direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0, pt: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{r.paper.subject}</Typography>
              </Box>
              <TextField
                size="small"
                value={r.absent ? '' : r.value}
                placeholder={r.absent ? 'Absent' : '—'}
                onChange={(e) => set(idx, { value: e.target.value.replace(/[^\d.]/g, '') })}
                disabled={r.absent}
                error={!!errorFor(r)}
                helperText={errorFor(r) || ' '}
                inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                InputProps={{ endAdornment: <InputAdornment position="end">/ {r.paper.maxMarks}</InputAdornment> }}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                sx={{ m: 0, pt: 0.5 }}
                control={<Switch size="small" checked={r.absent} onChange={(e) => set(idx, { absent: e.target.checked })} />}
                label={<Typography variant="caption" color="text.secondary">Absent</Typography>}
              />
            </Stack>
          ))}
          <Typography variant="caption" color="text.secondary">
            Leave a paper blank to mark it later. An absent paper counts as zero.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || invalid || filled.length === 0}>
          {busy ? 'Saving…' : 'Save marks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestScoresDialog;
